import { PaletteMode } from '@mui/material';
import { createTheme, SimplePaletteColorOptions, ThemeOptions } from '@mui/material/styles';
import { Shadows } from '@mui/material/styles/shadows';
import {
  black,
  blue,
  blue2,
  grey,
  grey2,
  red,
  white,
  grey4,
  grey6,
  yellow,
  grey9,
  black1,
  yellow1,
  black2,
  grey11,
  grey10,
  green,
  green1,
  green2,
  grey3,
  red2,
  yellowDefault,
  yellowHover,
  yellowActive,
  blueDefault,
  blueHover,
  blueActive,
  grayDefault,
  grayHover,
  grayActive,
  outlineDefault,
  outlineHover,
  outlineActive,
  redDefault,
  redHover,
  redActive,
  greenDefault,
  greenHover,
  greenActive,
} from './colors';
import { btn } from 'app/components/PageHeader/PageHeader.sx';

export type ColorMode = PaletteMode | 'system';

export const px4 = '0.25rem';
export const px6 = '0.375rem';
export const px8 = '0.5rem';
export const px9 = '0.5625rem';
export const px10 = '0.625rem';
export const px12 = '0.75rem';
export const px14 = '0.875rem';
export const px16 = '1rem';
export const px18 = '1.125rem';
export const px20 = '1.25rem';
export const px22 = '1.375rem';
export const px24 = '1.5rem';
export const px28 = '1.75rem';
export const px32 = '2rem';
export const px34 = '2.125rem';
export const px36 = '2.25rem';
export const px38 = '2.375rem';
export const px40 = '2.5rem';
export const px42 = '2.625rem';
export const px48 = '3rem';
export const px52 = '3.25rem';
export const px54 = '3.375rem';
export const px64 = '4rem';
export const px66 = '4.125rem';
export const px92 = '5.75rem';
export const px100 = '6.25rem';
export const px109 = '6.8125rem';
export const px168 = '10.5rem';
export const px172 = '10.75rem';
export const px198 = '12.375rem';
export const px200 = '12.5rem';
export const px290 = '18.125rem';
export const px368 = '23rem';
export const px424 = '26.5rem';

declare module '@mui/material/styles' {
  interface Palette {
    normal: SimplePaletteColorOptions;
    tertiary: Palette['primary'];
  }

  interface PaletteOptions {
    normal?: SimplePaletteColorOptions;
    tertiary?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}

export const getTheme = (_mode: PaletteMode | ColorMode) => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: blue,
      },
      secondary: {
        main: black,
        light: black,
        dark: black,
      },
      tertiary: {
        main: grey11,
      },
      background: {
        default: white,
        paper: white,
      },
      text: {
        primary: black,
        secondary: black,
        quaternary: green,
      },
      // error: {
      //   main: 'rgb(255, 58, 32)',
      //   light: 'rgb(255, 235, 233)',
      //   dark: 'rgb(102, 23, 13)',
      // },
      // warning: {
      //   main: 'rgb(255, 153, 0)',
      //   light: 'rgb(255, 245, 229)',
      //   dark: 'rgb(102, 61, 0)',
      // },
      // success: {
      //   main: 'rgb(41, 216, 132)',
      //   light: 'rgb(234, 251, 243)',
      //   dark: 'rgb(16, 86, 53)',
      // },
      // normal: {
      //   main: 'rgb(255, 226, 71)',
      //   light: 'rgb(255, 252, 237)',
      //   dark: 'rgb(102, 90, 28)',
      // },
      // info: {
      //   main: 'rgb(92, 200, 255)',
      //   light: 'rgb(239, 249, 255)',
      //   dark: 'rgb(37, 80, 102)',
      // },
      // divider: dark ? "#fff" : '#333333',
      // action: {
      //     hoverOpacity: 0.3,
      // },
    },

    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        // most basic recommended timing
        standard: 300,
        // this is to be used in complex animations
        complex: 375,
        // recommended when something is entering screen
        enteringScreen: 225,
        // recommended when something is leaving screen
        leavingScreen: 195,
      },
      easing: {
        // This is the most common easing curve.
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        // Objects enter the screen at full velocity from off-screen and
        // slowly decelerate to a resting point.
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        // Objects leave the screen at full velocity. They do not decelerate when off-screen.
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        // The sharp curve is used by objects that may return to the screen at any time.
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
          focusRipple: false,
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          disableElevation: true,
          size: 'large',
          color: 'primary',
        },
        styleOverrides: {
          root: {
            padding: '8px 16px',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '22px',
            textTransform: 'unset',
            borderRadius: '8px',
            fontFamily: 'Yelo',
            gap: '8px',
            color: black1,

            '& .MuiSvgIcon-root': {
              height: '22px',
              width: '22px',
            },

            '& .MuiButton-startIcon': {
              marginRight: '10px',
            },

            '& .MuiButton-endIcon': {
              marginLeft: '10px',
            },

            '&.MuiButton-containedSizeSmall': {
              padding: '8px 8px',
              fontSize: '12px !important',
              lineHeight: '16px',

              '& .MuiSvgIcon-root': {
                height: '16px',
                width: '16px',
              },
            },

            '&.MuiButton-containedSizeMedium': {
              padding: '8px 12px',
              fontSize: '14px',
              lineHeight: '20px',
              gap: '8px',
              '& .MuiSvgIcon-root': {
                height: '18px',
                width: '18px',
              },
            },

            '&.MuiButton-containedSizeLarge': {
              padding: '8px 16px',
              fontSize: '16px',
              lineHeight: '24px',

              '& .MuiSvgIcon-root': {
                height: '20px',
                width: '20px',
              },
            },

            '&:disabled': {
              color: white,
              backgroundColor: grey6,
            },
          },
          textPrimary: {
            color: black,
            backgroundColor: 'transparent',
            fontWeight: '600',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
          outlinedPrimary: {
            color: black,
            backgroundColor: 'transparent',
            borderWidth: '1px',
            borderColor: '#14191F33',
            '&:hover': {
              background: outlineHover,
            },
            '&:active': {
              background: outlineActive,
              // background: outlineActive,
            },
          },
          containedPrimary: {
            color: black1,
            backgroundColor: yellowDefault,
            '&:hover': {
              backgroundColor: yellowHover,
            },
            '&:active': {
              backgroundColor: yellowActive,
            },
          },
          outlinedSecondary: {
            color: black,
            backgroundColor: 'transparent',
            borderWidth: '0px',
            border: 0,
            '&:hover': {
              backgroundColor: outlineHover,
              border: 0,
            },
            '&:active': {
              background: outlineActive,
              border: 0,
            },
          },
          containedSecondary: {
            color: black,
            backgroundColor: grayDefault,
            // padding: '6px 18px',
            '&:hover': {
              backgroundColor: grayHover,
            },
            '&:active': {
              backgroundColor: grayActive,
            },
          },
          containedTertiary: {
            color: black1,
            background: grey10,
            '&:hover': {
              background: grey9,
            },
          },
          containedInfo: {
            color: white,
            background: blueDefault,
            fontWeight: 400,
            '&:hover': {
              background: blueHover,
            },
            '&:active': {
              backgroundColor: blueActive,
            },
            minWidth: '0px',
          },
          containedSuccess: {
            color: white,
            background: greenDefault,
            fontWeight: 400,
            '&:hover': {
              background: greenHover,
            },
            '&:active': {
              backgroundColor: greenActive,
            },
            minWidth: '0px',
          },
          containedWarning: {
            color: white,
            background: redDefault,
            '&:hover': {
              background: redHover,
            },
            '&:active': {
              backgroundColor: redActive,
            },
          },
        },
      },
      // MuiIconButton: {
      //   defaultProps: {
      //     size: 'medium',
      //     color: 'primary',
      //   },
      //   styleOverrides: {
      //     root: {
      //       padding: '15px',
      //       borderRadius: '12px',
      //       backgroundColor: white,
      //       '& .MuiSvgIcon-root': {
      //         height: '18px',
      //         width: '18px',
      //       },
      //     },
      //     colorPrimary: {
      //       border: `1px solid ${blue}`,
      //     },
      //     colorSecondary: {
      //       border: `1px solid ${black}`,
      //     },
      //   },
      // },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              '& fieldset': {
                borderColor: `${grey4}`,
                borderRadius: '10px',
              },

              '& .MuiInputAdornment-positionStart': {
                marginRight: '-4px',
              },
              '& .MuiInputAdornment-positionEnd': {
                marginLeft: '-4px',
              },

              '& .Mui-error': {
                '& fieldset.MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${red2}`,
                },
              },
            },
            '& .MuiInputBase-input': {
              padding: '12px 16px',
              color: black,
              backgroundColor: white,
              borderRadius: '10px',

              '&::placeholder': {
                color: grey,
                opacity: 'unset',
              },
            },
            '& .MuiInputLabel-root': {
              top: '3px',
              color: grey,

              '& .MuiInputLabel-asterisk': {
                color: red2,
              },
            },
            '& .Mui-error': {
              color: red2,
            },
            '& .MuiFormHelperText-root': {
              color: grey,
              '&.Mui-error': {
                color: red2,
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            height: '49px',

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: grey4,
            },

            '&.Mui-disabled': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(20, 25, 31, 0.10)',
              },

              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(20, 25, 31, 0.10)',
                cursor: 'not-allowed',
              },
            },

            '&.Mui-error': {
              '& .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${red2}`,
              },

              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: grey,
              },
            },

            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${grey}`,
              },

              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: grey,
              },
            },

            '& fieldset': {
              borderColor: grey4,
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: grey,
            },

            '& .MuiInputBase-input': {
              padding: '14px 16px',
              color: black,
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
              fontFamily: 'Yelo',
              fontWeight: 400,

              '&::placeholder': {
                color: grey,
                opacity: 'unset',
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: '-3px 3px 12px 0px #2D2C2C0F!important',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: grey,
            backgroundColor: white,
            '&.Mui-selected': {
              color: blue,
              backgroundColor: white,
              '&:hover': {
                backgroundColor: white,
              },
            },
            '&:hover': {
              color: blue,
              backgroundColor: white,
            },
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          color: 'text.primary',
          fontFamily: 'Yelo',
        },
      },
      // MuiTable: {
      //   defaultProps: {
      //     stickyHeader: true,
      //   },
      //   styleOverrides: {
      //     root: {
      //       borderCollapse: 'separate',
      //       borderSpacing: '0px 6px',
      //       minWidth: '650px',
      //     },
      //   },
      // },
      // MuiTableRow: {
      //   styleOverrides: {
      //     root: {
      //       backgroundColor: white,
      //       border: `1px solid ${grey1}`,
      //       boxShadow: '-3px 3px 12px 0px #ADAFC017',
      //     },
      //   },
      // },
      // MuiTableHead: {
      //   styleOverrides: {
      //     root: {
      //       backgroundColor: white,
      //       border: `1px solid ${grey1}`,
      //
      //       '& .MuiTableCell-root': {
      //         fontStyle: 'normal',
      //         fontWeight: 600,
      //         fontSize: '12px',
      //         lineHeight: '20px',
      //         color: grey7,
      //         padding: '11px',
      //         textTransform: 'uppercase',
      //         userSelect: 'none',
      //
      //         '&:first-of-type': {
      //           borderTopLeftRadius: '12px!important',
      //           borderBottomLeftRadius: '12px!important',
      //           paddingLeft: '22px',
      //         },
      //
      //         '&:last-of-type': {
      //           borderTopRightRadius: '12px!important',
      //           borderBottomRightRadius: '12px!important',
      //           paddingRight: '22px',
      //         },
      //       },
      //     },
      //   },
      // },
      // MuiTableCell: {
      //   styleOverrides: {
      //     root: {
      //       fontStyle: 'normal',
      //       fontWeight: 400,
      //       fontSize: '14px',
      //       lineHeight: '22px',
      //       color: black,
      //       border: 'none',
      //       padding: '18px',
      //       width: 'calc(100% - 20px)',
      //       marginBottom: '40px',
      //       '&:first-of-type': {
      //         borderTopLeftRadius: '18px!important',
      //         borderBottomLeftRadius: '18px!important',
      //         paddingLeft: '24px',
      //       },
      //
      //       '&:last-of-type': {
      //         borderTopRightRadius: '18px!important',
      //         borderBottomRightRadius: '18px!important',
      //         paddingRight: '24px',
      //       },
      //     },
      //   },
      // },
      MuiPagination: {
        styleOverrides: {
          root: {
            '& ul': {
              '& li': {
                '&:first-of-type': {
                  // border: '1px solid red',
                  marginRight: '24px',
                },

                '&:last-of-type': {
                  // border: '1px solid green',
                  marginLeft: '24px',
                },
              },
            },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            padding: 0,
            minWidth: 'unset',
            '&.MuiPaginationItem-root': {
              backgroundColor: 'unset',
              padding: 0,
              minWidth: 'unset',

              '&.MuiPaginationItem-page': {
                color: grey,
                '&.Mui-selected': {
                  color: black,
                },
              },

              '&.MuiPaginationItem-previousNext': {
                width: '36px',
                height: '36px',
                borderRadius: '12px',
                background: white,

                '&.Mui-disabled': {
                  backgroundColor: grey2,
                },
              },
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: white,
            minHeight: 'unset',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            borderRadius: '11px',
            border: `1px solid transparent`,
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '22px',
            color: black,
            padding: '12px 24px',
            minHeight: 'unset',
            minWidth: '90px',
            textTransform: 'unset',

            '&.Mui-selected': {
              color: blue,
              backgroundColor: 'transparent',
              fontWeight: '600',
            },
          },
        },
      },
    },

    typography: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontStyle: 'normal',
        lineHeight: '30px',
        fontWeight: 700,
        fontSize: '28px',
        color: black,
      },
      h2: {
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '24px',
        color: black,
      },
      h3: {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '22px',
        color: black,
      },
      // h4: {
      //   fontSize: px18,
      //   fontWeight: 'bold',
      //   lineHeight: px24,
      // },
      // body1: {
      //   fontSize: px16,
      //   fontWeight: 'normal',
      //   lineHeight: px22,
      // },
      // body2: {
      //   fontSize: px14,
      //   fontWeight: 'normal',
      //   lineHeight: px20,
      // },
      // subtitle1: {
      //   fontSize: px16,
      //   fontWeight: 'bold',
      //   lineHeight: px22,
      // },
      // subtitle2: {
      //   fontSize: px16,
      //   fontWeight: 'bold',
      //   lineHeight: px22,
      // },
      // caption: {
      //   fontSize: px12,
      //   fontWeight: 'normal',
      //   lineHeight: px16,
      //   letterSpacing: '0.4px',
      // },
      // overline: {
      //   fontSize: px12,
      //   fontWeight: 'normal',
      //   lineHeight: px16,
      //   letterSpacing: '1px',
      //   textTransform: 'uppercase',
      // },
    },
    shape: {
      borderRadius: 10,
    },
    shadows: Array(25).fill('none') as Shadows,
    // spacing: 4,
    breakpoints: {
      values: {
        xs: 0,
        sm: 576,
        md: 992,
        lg: 1300,
        xl: 1536,
      },
    },
  } as ThemeOptions);
};

export { useTheme } from '@mui/material/styles';
